import { useMemo } from "react";
import {
  useTelemetryMetadata,
  useTelemetrySummary,
} from "@/hooks/useTelemetry";

export type TelemetryTimestampSource =
  | "telemetry_metadata"
  | "telemetry_summary"
  | "session_database";

/**
 * Hook to extract the recording timestamp from telemetry data with fallback to session.conductedAt
 *
 * Priority order:
 * 1. recordingTimestamp from telemetry metadata (extracted from actual telemetry file)
 * 2. recordingTimestamp from telemetry summary (fallback if metadata doesn't have it)
 * 3. session.conductedAt (final fallback from database)
 */
export function useTelemetryTimestamp(
  sessionId: string,
  sessionConductedAt?: string,
) {
  const { data: metadata } = useTelemetryMetadata(sessionId);
  const { data: summary } = useTelemetrySummary(sessionId);

  const telemetryTimestamp = useMemo(() => {
    // Priority 1: Check telemetry metadata for recording timestamp
    if (metadata?.recordingTimestamp) {
      return metadata.recordingTimestamp;
    }

    // Priority 2: Check telemetry summary for recording timestamp
    if (summary?.recordingTimestamp) {
      return summary.recordingTimestamp;
    }

    // Priority 3: Fallback to session.conductedAt
    return sessionConductedAt;
  }, [
    metadata?.recordingTimestamp,
    summary?.recordingTimestamp,
    sessionConductedAt,
  ]);

  const timestampSource = useMemo((): TelemetryTimestampSource => {
    if (metadata?.recordingTimestamp) {
      return "telemetry_metadata";
    }
    if (summary?.recordingTimestamp) {
      return "telemetry_summary";
    }
    return "session_database";
  }, [metadata?.recordingTimestamp, summary?.recordingTimestamp]);

  return {
    timestamp: telemetryTimestamp,
    source: timestampSource,
    hasTelemetryTimestamp: !!(
      metadata?.recordingTimestamp || summary?.recordingTimestamp
    ),
  };
}
